import React, { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  CalendarCogIcon,
  CalendarMinusIcon,
  CalendarPlusIcon,
  ExternalLinkIcon,
  EyeIcon,
  FileMinusIcon,
  FilePenIcon,
  FilePlusIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react'

import { cn } from '@/lib/utils.ts'
import { DateFormat } from '@/components/date-format.tsx'
import { Empty } from '@/components/empty.tsx'
import { QuerySpinner } from '@/components/spinner/query-spinner.tsx'
import { Document } from '@/features/documents/types.ts'
import { useFindLogs } from '@/features/logs/hooks/use-find-logs.ts'

import { DocumentChangesDialog } from './document-changes-dialog.tsx'

interface AffectedRecord {
  model: string
  id: string
}

function getCalendarEventId(
  affectedRecords?: AffectedRecord[]
): string | undefined {
  return affectedRecords?.find((r) => r.model === 'calendarEvent')?.id
}

function isDocumentChange(log: {
  code: string
  affected_records?: AffectedRecord[]
}): boolean {
  return (
    log.code === 'record_changed' &&
    log.affected_records?.[0]?.model === 'document'
  )
}

const modelNameToLabel: Record<string, string> = {
  document: 'Documento',
  calendarEvent: 'Evento calendario',
  attachment: 'Allegato',
}

const codeToLabel: Record<string, string> = {
  record_created: 'creato',
  record_changed: 'modificato',
  record_deleted: 'eliminato',
} as const

const codeToIconMap: Record<
  string,
  typeof PencilIcon | Record<string, typeof PencilIcon>
> = {
  record_created: PlusIcon,
  record_changed: PencilIcon,
  record_deleted: Trash2Icon,
  calendarEvent: {
    record_created: CalendarPlusIcon,
    record_changed: CalendarCogIcon,
    record_deleted: CalendarMinusIcon,
  },
  attachment: {
    record_created: FilePlusIcon,
    record_changed: FilePenIcon,
    record_deleted: FileMinusIcon,
  },
}

function getIcon({
  code,
  model,
}: {
  code?: string | null
  model?: string | null
}) {
  const icon =
    // @ts-expect-error I know TODO
    codeToIconMap[model ?? '']?.[code ?? ''] ?? codeToIconMap[code ?? '']
  if (!icon) return <></>

  const a = { icon }
  return <a.icon aria-hidden="true" className="size-4 text-white" />
}

export const LogsTab: React.FC<{ document?: Document }> = ({ document }) => {
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null)

  const findLogsQuery = useFindLogs(
    { record_ids: [document?.id as string] },
    {
      enabled: !!document?.id,
    }
  )
  type LogWithUser = NonNullable<typeof findLogsQuery.data>['data'][number] & {
    user?: { id: string; email: string; name: string } | null
    transaction_id?: string | null
  }
  const logs = findLogsQuery.data?.data as LogWithUser[] | undefined

  const icons = useMemo(
    () =>
      logs?.map((l) =>
        getIcon({ code: l.code, model: l.affected_records?.[0]?.model })
      ) ?? [],
    [logs]
  )

  return (
    <>
      <DocumentChangesDialog
        transactionId={selectedTransactionId}
        isOpen={!!selectedTransactionId}
        onClose={() => setSelectedTransactionId(null)}
      />

      <QuerySpinner query={findLogsQuery} wrapperClassName="py-10">
        <>
          {!logs?.length && <Empty title="Nessun log disponibile" />}

          {!!logs?.length && (
            <div className="flow-root py-4">
              <ul role="list" className="-mb-8">
                {logs?.map((log, logIdx) => {
                  const calendarEventId = getCalendarEventId(
                    log.affected_records as AffectedRecord[]
                  )
                  const hasDocumentChanges =
                    isDocumentChange({
                      code: log.code,
                      affected_records:
                        log.affected_records as AffectedRecord[],
                    }) && log.transaction_id

                  const titleText = `${
                    log.affected_records?.[0]?.model
                      ? modelNameToLabel[log.affected_records?.[0]?.model]
                      : 'Record'
                  } ${codeToLabel[log.code]}`

                  const hasAction = !!calendarEventId || hasDocumentChanges

                  const rowContent = (
                    <>
                      <div>
                        <span
                          className={cn(
                            'flex size-8 items-center justify-center rounded-full ring-8 ring-white',
                            'bg-gray-400',
                            hasAction && 'group-hover:ring-gray-100'
                          )}
                        >
                          {icons[logIdx]}
                        </span>
                      </div>
                      <div className="flex min-w-0 flex-1 items-center justify-between space-x-4">
                        <div>
                          <p className="flex items-center gap-1.5 text-sm text-gray-500">
                            <span>{titleText}</span>
                            {calendarEventId ? (
                              <ExternalLinkIcon className="size-3.5 text-gray-400" />
                            ) : hasDocumentChanges ? (
                              <EyeIcon className="size-3.5 text-gray-400" />
                            ) : (
                              <span
                                className="inline-block size-3.5"
                                aria-hidden="true"
                              />
                            )}
                          </p>
                          <p className="text-xs text-gray-400">
                            {log.user?.email ?? log.user_id}
                          </p>
                        </div>
                        <div className="shrink-0 whitespace-nowrap text-right text-sm text-gray-500">
                          <DateFormat
                            date={log.created_at}
                            format="DD/MM/YYYY HH:mm"
                          />
                        </div>
                      </div>
                    </>
                  )

                  return (
                    <li key={log.id}>
                      <div className="relative pb-8">
                        {logIdx !== logs.length - 1 ? (
                          <span
                            aria-hidden="true"
                            className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                          />
                        ) : null}
                        {calendarEventId ? (
                          <Link
                            to="/calendar"
                            search={{ eventId: calendarEventId }}
                            className="group relative -mx-2 flex w-full items-center space-x-3 rounded-lg px-2 py-1 transition-colors hover:bg-gray-100"
                          >
                            {rowContent}
                          </Link>
                        ) : hasDocumentChanges ? (
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedTransactionId(log.transaction_id!)
                            }
                            className="group relative -mx-2 flex w-full items-center space-x-3 rounded-lg px-2 py-1 text-left transition-colors hover:bg-gray-100"
                          >
                            {rowContent}
                          </button>
                        ) : (
                          <div className="relative -mx-2 flex w-full items-center space-x-3 px-2 py-1">
                            {rowContent}
                          </div>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </>
      </QuerySpinner>
    </>
  )
}
